import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { classLabel } from './contexts';
import { type Db, must, useDb } from './db';

export interface Material {
  id: string;
  courseId: string;
  subject: string;
  classLabel: string;
  lessonId: string | null;
  title: string;
  url: string | null;
  storagePath: string | null;
  mimeType: string | null;
  sizeBytes: number | null;
  createdAt: string;
}

/** Up to 20 MB, the limit of the bucket. */
export const MATERIAL_MAX_BYTES = 20 * 1024 * 1024;

const SELECT =
  'id, course_id, lesson_id, title, url, storage_path, mime_type, size_bytes, created_at, courses (group_label, subjects (name), classes (grade, letter))';

/** Materials of some courses, or every material the person may see in a school. */
export function useMaterials(filter: { courseId: string } | { schoolId: string }) {
  const db = useDb();
  return useQuery({
    queryKey: ['materials', filter],
    enabled: 'courseId' in filter ? filter.courseId !== '' : filter.schoolId !== '',
    queryFn: async () => {
      let query = db
        .from('lesson_materials')
        .select(SELECT)
        .order('created_at', { ascending: false });
      query =
        'courseId' in filter
          ? query.eq('course_id', filter.courseId)
          : query.eq('school_id', filter.schoolId);
      return must(await query).map((r): Material => ({
        id: r.id,
        courseId: r.course_id,
        subject:
          (r.courses?.subjects?.name ?? '') +
          (r.courses?.group_label ? ` (${r.courses.group_label})` : ''),
        classLabel: r.courses?.classes ? classLabel(r.courses.classes) : '',
        lessonId: r.lesson_id,
        title: r.title,
        url: r.url,
        storagePath: r.storage_path,
        mimeType: r.mime_type,
        sizeBytes: r.size_bytes,
        createdAt: r.created_at,
      }));
    },
  });
}

/** A file name the storage accepts: Latin letters, digits, dots, dashes. */
export function storageFileName(name: string): string {
  const dot = name.lastIndexOf('.');
  const ext =
    dot > 0
      ? name
          .slice(dot + 1)
          .toLowerCase()
          .replace(/[^a-z0-9]/g, '')
      : '';
  const base = (dot > 0 ? name.slice(0, dot) : name)
    .normalize('NFKD')
    .replace(/[^A-Za-z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
  return `${base || 'file'}${ext ? `.${ext.slice(0, 10)}` : ''}`;
}

/** A link valid for a minute: the file is read only while its row is visible. */
export async function materialLink(db: Db, material: Pick<Material, 'url' | 'storagePath'>) {
  if (material.url) return material.url;
  const { data, error } = await db.storage
    .from('materials')
    .createSignedUrl(material.storagePath as string, 60);
  if (error) throw error;
  return data.signedUrl;
}

export function useMaterialActions(schoolId: string, addedBy: string) {
  const db = useDb();
  const client = useQueryClient();
  const invalidate = () => client.invalidateQueries({ queryKey: ['materials'] });
  return {
    addLink: useMutation({
      mutationFn: async (a: { courseId: string; title: string; url: string; lessonId?: string }) =>
        must(
          await db.from('lesson_materials').insert({
            school_id: schoolId,
            course_id: a.courseId,
            lesson_id: a.lessonId ?? null,
            title: a.title.trim(),
            url: a.url.trim(),
            added_by_member_id: addedBy,
          }),
        ),
      onSettled: invalidate,
    }),
    addFile: useMutation({
      mutationFn: async (a: { courseId: string; title: string; file: File }) => {
        const id = globalThis.crypto.randomUUID();
        const path = `${schoolId}/${a.courseId}/${id}/${storageFileName(a.file.name)}`;
        must(
          await db.from('lesson_materials').insert({
            id,
            school_id: schoolId,
            course_id: a.courseId,
            title: a.title.trim() || a.file.name,
            storage_path: path,
            mime_type: a.file.type || null,
            size_bytes: a.file.size,
            added_by_member_id: addedBy,
          }),
        );
        const { error } = await db.storage
          .from('materials')
          .upload(path, a.file, { contentType: a.file.type || 'application/octet-stream' });
        if (error) {
          // the row without its file would be a broken link
          await db.from('lesson_materials').delete().eq('id', id);
          throw error;
        }
      },
      onSettled: invalidate,
    }),
    remove: useMutation({
      mutationFn: async (m: Pick<Material, 'id' | 'storagePath'>) => {
        // the file first: its permission needs the row
        if (m.storagePath) {
          const { error } = await db.storage.from('materials').remove([m.storagePath]);
          if (error) throw error;
        }
        must(await db.from('lesson_materials').delete().eq('id', m.id));
      },
      onSettled: invalidate,
    }),
  };
}

/** «PDF · 1,2 МБ» */
export function fileLabel(m: Pick<Material, 'mimeType' | 'sizeBytes' | 'storagePath'>): string {
  const ext = m.storagePath?.split('.').pop()?.toUpperCase() ?? '';
  if (m.sizeBytes === null) return ext;
  const size =
    m.sizeBytes >= 1024 * 1024
      ? `${(m.sizeBytes / 1024 / 1024).toFixed(1).replace('.', ',')} МБ`
      : `${Math.max(1, Math.round(m.sizeBytes / 1024))} КБ`;
  return ext ? `${ext} · ${size}` : size;
}
